/**
 * Locale-based formatting utilities for the Belgacoin platform
 * 
 * This module provides utility functions for formatting dates, times, and currency values
 * using the user's locale from react-intl context. These functions leverage the Intl API
 * for proper internationalization support.
 * 
 * TIMEZONE HANDLING:
 * All timestamp formatting functions automatically convert UTC timestamps from the API
 * to the user's local timezone using Intl.DateTimeFormat. The browser's timezone is
 * used by default when no explicit timezone is specified in the options.
 * 
 * Requirements: 6.2, 6.3, 9.4
 */

/**
 * Format a timestamp for display based on time range context
 * 
 * Automatically converts UTC timestamps from the API to the user's local timezone.
 * The Intl.DateTimeFormat API uses the browser's timezone by default.
 * 
 * @param {number|Date} timestamp - The timestamp to format (Unix timestamp in ms or Date object)
 * @param {string} timeRange - The time range context ('24h', '7d', '30d', '1y')
 * @param {string} locale - The user's locale (e.g., 'en-US', 'de-DE', 'fr-FR')
 * @returns {string} Formatted date/time string in user's local timezone
 */
export const formatTimestamp = (timestamp, timeRange, locale) => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
  
  switch (timeRange) {
    case "24h":
      // Show full date and time for 24-hour range
      return new Intl.DateTimeFormat(locale, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date)
      
    case "7d":
      // Show date and time for 7-day range
      return new Intl.DateTimeFormat(locale, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date)
      
    case "30d":
      // Show date for 30-day range
      return new Intl.DateTimeFormat(locale, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date)
      
    case "1y":
      // Show month and year for 1-year range
      return new Intl.DateTimeFormat(locale, {
        month: "long",
        year: "numeric",
      }).format(date)
      
    default:
      return new Intl.DateTimeFormat(locale, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date)
  }
}

/**
 * Format a timestamp for X-axis tick labels based on time range
 * 
 * Automatically converts UTC timestamps from the API to the user's local timezone.
 * 
 * @param {number|Date} timestamp - The timestamp to format
 * @param {string} timeRange - The time range context ('24h', '7d', '30d', '1y')
 * @param {string} locale - The user's locale
 * @returns {string} Formatted date/time string for axis labels in user's local timezone
 */
export const formatXAxisTick = (timestamp, timeRange, locale) => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
  
  switch (timeRange) {
    case "24h":
      // Show hours for 24-hour range
      return new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
      }).format(date)
      
    case "7d":
      // Show day and month for 7-day range
      return new Intl.DateTimeFormat(locale, {
        month: "short",
        day: "numeric",
      }).format(date)
      
    case "30d":
      // Show day and month for 30-day range
      return new Intl.DateTimeFormat(locale, {
        month: "short",
        day: "numeric",
      }).format(date)
      
    case "1y":
      // Show month and year for 1-year range
      return new Intl.DateTimeFormat(locale, {
        month: "short",
        year: "numeric",
      }).format(date)
      
    default:
      return new Intl.DateTimeFormat(locale, {
        month: "short",
        day: "numeric",
      }).format(date)
  }
}

/**
 * Format a currency value with locale-based formatting
 * 
 * @param {number} value - The numeric value to format
 * @param {string} currency - The currency code (e.g., 'EUR', 'USD')
 * @param {string} locale - The user's locale
 * @param {Object} options - Additional formatting options
 * @param {number} options.minimumFractionDigits - Minimum decimal places (default: 2)
 * @param {number} options.maximumFractionDigits - Maximum decimal places (default: 2)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (
  value,
  currency,
  locale,
  { minimumFractionDigits = 2, maximumFractionDigits = 2 } = {}
) => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value)
}

/**
 * Format a currency value for Y-axis tick labels
 * 
 * @param {number} value - The numeric value to format
 * @param {string} currency - The currency code
 * @param {string} locale - The user's locale
 * @returns {string} Formatted currency string for axis labels
 */
export const formatYAxisTick = (value, currency, locale) => {
  return formatCurrency(value, currency, locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * Format a currency value for tooltip display with higher precision
 * 
 * @param {number} value - The numeric value to format
 * @param {string} currency - The currency code
 * @param {string} locale - The user's locale
 * @returns {string} Formatted currency string with more decimal places
 */
export const formatTooltipValue = (value, currency, locale) => {
  return formatCurrency(value, currency, locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6, // Show more decimals for precision
  })
}
